import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { 
  DollarSign, 
  Sparkles, 
  Building2, 
  BarChart3,
  Users,
  FileText,
  Shield,
  ArrowRight
} from 'lucide-react';
import { cn } from '../lib/utils';

type FeatureCardProps = {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  color: string;
  comingSoon?: boolean;
};

function FeatureCard({ title, description, icon: Icon, href, color, comingSoon }: FeatureCardProps) {
  const navigate = useNavigate();

  return (
    <Card
      className={cn(
        "group cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1 border-stroke-primary",
        comingSoon && "opacity-75 cursor-not-allowed"
      )}
      onClick={() => !comingSoon && navigate(href)}
    >
      <CardHeader className="pb-3">
        <div className={cn(
          "flex h-12 w-12 items-center justify-center rounded-lg mb-2 transition-colors",
          color
        )}>
          <Icon className="h-6 w-6 text-white" />
        </div>
        <CardTitle className="text-lg flex items-center justify-between">
          {title}
          {comingSoon && (
            <span className="text-xs font-normal bg-badge-yellow text-text-primary px-2 py-0.5 rounded-full">
              Coming Soon
            </span>
          )}
        </CardTitle>
        <CardDescription className="text-sm">{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center text-sm text-text-link group-hover:gap-2 transition-all">
          {comingSoon ? (
            <span className="text-text-secondary">Available soon</span>
          ) : (
            <>
              <span>Explore</span>
              <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function HomePage() {
  const features: FeatureCardProps[] = [
    {
      title: 'Pricing Management',
      description: 'Manage firm pricing, subscriptions, and billing information',
      icon: DollarSign,
      href: '/pricing/firms',
      color: 'bg-button-primary',
    },
    {
      title: 'Features',
      description: 'Configure and manage platform features and capabilities',
      icon: Sparkles,
      href: '/features',
      color: 'bg-purple-500',
      comingSoon: true,
    },
    {
      title: 'User Management',
      description: 'Manage users, roles, and permissions across the platform',
      icon: Users,
      href: '/users',
      color: 'bg-blue-500',
      comingSoon: true,
    },
    {
      title: 'Analytics & Reports',
      description: 'View platform analytics, usage statistics, and generate reports',
      icon: BarChart3,
      href: '/analytics',
      color: 'bg-green-500',
      comingSoon: true,
    },
    {
      title: 'Content Management',
      description: 'Manage documents, templates, and content across the platform',
      icon: FileText,
      href: '/content',
      color: 'bg-orange-500',
      comingSoon: true,
    },
    {
      title: 'Security & Settings',
      description: 'Configure security settings, API keys, and system preferences',
      icon: Shield,
      href: '/settings',
      color: 'bg-red-500',
      comingSoon: true,
    },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Header Section */}
      <div className="border-b border-stroke-primary bg-background">
        <div className="p-8">
          <div className="max-w-4xl">
            <h1 className="text-4xl font-bold text-text-primary mb-3">
              Welcome to CQ Admin
            </h1>
            <p className="text-lg text-text-secondary">
              Manage your platform, configure settings, and monitor activity from one central dashboard.
            </p>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="border-b border-stroke-primary bg-surface-secondary">
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl">
            <div className="flex items-center space-x-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-button-primary">
                <Building2 className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-text-secondary">Total Firms</p>
                <p className="text-xl font-semibold text-text-primary">8</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-500">
                <Users className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-text-secondary">Active Users</p>
                <p className="text-xl font-semibold text-text-primary">-</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-500">
                <BarChart3 className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-text-secondary">Platform Health</p>
                <p className="text-xl font-semibold text-text-primary">100%</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="flex-1 overflow-auto p-8">
        <div className="max-w-6xl mx-auto">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-text-primary mb-2">
              Platform Features
            </h2>
            <p className="text-text-secondary">
              Access and manage all platform features from here
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => (
              <FeatureCard key={feature.href} {...feature} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
